(() => {
  if (customElements.get('ws-floating-video')) return;
  class FloatingVideo extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      if (this.parentElement !== document.body) {
        this.ownerSection = this.closest('.shopify-section');
        document.body.appendChild(this);
        return;
      }
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const on = (target, type, handler) => target.addEventListener(type, handler, { signal });
      this.preview = this.querySelector('.wsfv-preview');
      this.full = this.querySelector('.wsfv-full');
      this.dialog = this.querySelector('dialog');
      this.openButton = this.querySelector('.wsfv-open');
      this.storageKey = 'wsfv:collapsed:v1';
      this.card = this.querySelector('.wsfv-card');
      this.reopenButton = this.querySelector('.wsfv-reopen');
      this.dismissed = this.readCollapsed();
      this.setCollapsed(this.dismissed, false);
      this.hidden = !this.dismissed;
      on(this.reopenButton, 'click', () => {
        clearTimeout(this.timer);
        this.setCollapsed(false, true);
        this.ensurePreview();
        this.openButton.focus({ preventScroll: true });
      });
      on(window, 'storage', event => {
        if (event.key !== this.storageKey && event.key !== null) return;
        clearTimeout(this.timer);
        this.setCollapsed(this.readCollapsed(), false);
        if (!this.dismissed) this.ensurePreview();
      });
      on(this.openButton, 'pointerdown', e => {
        if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
        const rect = this.getBoundingClientRect();
        this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, left: rect.left, top: rect.top, moved: false };
        this.suppressClick = false;
        this.openButton.setPointerCapture(e.pointerId);
      });
      on(this.openButton, 'pointermove', e => {
        const d = this.drag;
        if (!d || d.id !== e.pointerId) return;
        const dx = e.clientX - d.x, dy = e.clientY - d.y;
        if (Math.hypot(dx, dy) > 7) d.moved = true;
        if (d.moved) { this.place(d.left + dx, d.top + dy); this.positioned = true; }
      });
      const end = e => {
        if (!this.drag || this.drag.id !== e.pointerId) return;
        this.suppressClick = this.drag.moved || e.type === 'pointercancel';
        this.drag = null;
        if (this.openButton.hasPointerCapture(e.pointerId)) this.openButton.releasePointerCapture(e.pointerId);
      };
      on(this.openButton, 'pointerup', end);
      on(this.openButton, 'pointercancel', end);
      on(this.openButton, 'lostpointercapture', e => {
        if (this.drag?.id === e.pointerId) { this.drag = null; this.suppressClick = true; }
      });
      on(this.openButton, 'click', e => {
        if (this.suppressClick && e.detail !== 0) { e.preventDefault(); this.suppressClick = false; return; }
        this.open();
      });
      on(this.querySelector('.wsfv-dismiss'), 'click', () => {
        clearTimeout(this.timer);
        this.setCollapsed(true, true);
        this.reopenButton.focus({ preventScroll: true });
      });
      on(this.querySelector('.wsfv-modal-close'), 'click', () => this.dialog.close());
      on(this.dialog, 'close', () => {
        this.full.pause();
        document.documentElement.style.overflow = this.previousOverflow;
        this.playPreview();
        if (!this.dismissed) this.openButton.focus({ preventScroll: true });
      });
      on(document, 'visibilitychange', () => {
        if (document.hidden) { this.preview.pause(); this.full.pause(); }
        else this.playPreview();
      });
      on(window, 'pageshow', () => {
        if (this.readCollapsed() !== this.dismissed) {
          this.setCollapsed(this.readCollapsed(), false);
          if (!this.dismissed) this.ensurePreview();
        }
      });
      on(this.full, 'loadedmetadata', () => this.fitVideo());
      const resize = () => {
        if (this.dialog.open) this.fitVideo();
        if (this.positioned) { const r = this.getBoundingClientRect(); this.place(r.left, r.top); }
      };
      on(window, 'resize', resize);
      if (window.visualViewport) { on(window.visualViewport, 'resize', resize); on(window.visualViewport, 'scroll', resize); }
      on(document, 'shopify:section:unload', e => {
        if (e.target === this.ownerSection || e.detail?.sectionId === this.dataset.sectionId) this.remove();
      });
      this.timer = setTimeout(() => {
        if (!this.isConnected || this.dismissed) return;
        this.hidden = false;
        this.ensurePreview();
      }, 4000);
    }
    readCollapsed() {
      try { return localStorage.getItem(this.storageKey) === '1'; }
      catch { return this.dismissed || false; }
    }
    setCollapsed(collapsed, persist) {
      this.dismissed = collapsed;
      this.dataset.collapsed = String(collapsed);
      this.card.hidden = collapsed;
      this.reopenButton.hidden = !collapsed;
      if (collapsed) { this.preview.pause(); this.hidden = false; }
      if (persist) {
        try { localStorage.setItem(this.storageKey, collapsed ? '1' : '0'); }
        catch {}
      }
      if (this.positioned) {
        const rect = this.getBoundingClientRect();
        this.place(rect.left, rect.top);
      }
    }
    ensurePreview() {
      if (!this.preview.getAttribute('src')) this.preview.src = this.preview.dataset.src;
      this.playPreview();
    }
    place(left, top) {
      const viewport = window.visualViewport;
      const x = viewport?.offsetLeft || 0, y = viewport?.offsetTop || 0;
      const w = viewport?.width || document.documentElement.clientWidth;
      const h = viewport?.height || window.innerHeight;
      const r = this.getBoundingClientRect(), pad = 12;
      this.style.right = 'auto'; this.style.bottom = 'auto';
      this.style.left = Math.max(x + pad, Math.min(left, x + w - r.width - pad)) + 'px';
      this.style.top = Math.max(y + pad, Math.min(top, y + h - r.height - pad)) + 'px';
    }
    playPreview() {
      if (this.dismissed || this.dialog.open || document.hidden || !this.preview.getAttribute('src')) return;
      this.preview.muted = true;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.preview.play().catch(() => {});
    }
    fitVideo() {
      const w = window.visualViewport?.width || document.documentElement.clientWidth;
      const h = window.visualViewport?.height || window.innerHeight;
      this.dialog.style.setProperty('width', w + 'px', 'important');
      this.dialog.style.setProperty('height', h + 'px', 'important');
      const vw = this.full.videoWidth, vh = this.full.videoHeight;
      if (!vw || !vh) return;
      const scale = Math.min(w / vw, h / vh, 1);
      this.full.style.setProperty('width', Math.floor(vw * scale) + 'px', 'important');
      this.full.style.setProperty('height', Math.floor(vh * scale) + 'px', 'important');
    }
    open() {
      if (this.dialog.open || this.dismissed) return;
      this.preview.pause();
      if (!this.full.getAttribute('src')) this.full.src = this.dataset.fullSrc;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      this.fitVideo();
      document.documentElement.style.overflow = 'hidden';
      if (this.full.ended) this.full.currentTime = 0;
      this.full.play().catch(() => {});
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow;
      }
      this.abort?.abort(); this.abort = null;
      this.preview?.pause(); this.full?.pause();
    }
  }
  customElements.define('ws-floating-video', FloatingVideo);
})();
